import {
  ChatParticipantType,
  MeetingStatus,
  MeetingType,
  ParticipantType,
  reportingPrisma,
} from "client";
import type {
  EventTierAnalyticsDbFilters,
  EventTierCompanyPortalStats,
} from "./types";
import { analyticsCreatedAt, withCreatedAt } from "./prisma-date-range";

const IN_BATCH = 500;

type CreatedAtRange = Extract<
  ReturnType<typeof analyticsCreatedAt>,
  { mode: "bounded" | "unbounded" }
>;

function withStartDate<T extends Record<string, unknown>>(
  where: T,
  range: CreatedAtRange
): T {
  if (range.mode === "bounded") {
    return { ...where, startDate: range.createdAt };
  }
  return where;
}

function companyChatType(
  companyType: "exhibitor" | "sponsor"
): ChatParticipantType {
  return companyType === "sponsor"
    ? ChatParticipantType.SPONSOR
    : ChatParticipantType.EXHIBITOR;
}

function companyMeetingType(
  companyType: "exhibitor" | "sponsor"
): ParticipantType {
  return companyType === "sponsor"
    ? ParticipantType.SPONSOR
    : ParticipantType.EXHIBITOR;
}

async function mapInBatches<T, R>(
  values: T[],
  mapper: (batch: T[]) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  for (let index = 0; index < values.length; index += IN_BATCH) {
    results.push(await mapper(values.slice(index, index + IN_BATCH)));
  }
  return results;
}

async function countCompanyMeetings(
  eventId: string,
  companyId: string,
  meetingType: ParticipantType,
  range: CreatedAtRange
): Promise<number> {
  const participants = await reportingPrisma.meetingParticipant.findMany({
    where: { entityId: companyId, entityType: meetingType },
    select: { meetingId: true },
  });
  const meetingIds = [
    ...new Set(participants.map(({ meetingId }) => meetingId)),
  ];
  if (meetingIds.length === 0) return 0;

  const counts = await mapInBatches(meetingIds, (batch) =>
    reportingPrisma.meeting.count({
      where: withStartDate(
        {
          id: { in: batch },
          eventId,
          type: { not: MeetingType.SESSION },
          status: { not: MeetingStatus.REJECTED },
        },
        range
      ),
    })
  );
  return counts.reduce((total, count) => total + count, 0);
}

async function countCompanyMessagesReceived(
  eventId: string,
  companyId: string,
  chatType: ChatParticipantType,
  range: CreatedAtRange
): Promise<number> {
  const participants = await reportingPrisma.chatParticipant.findMany({
    where: { participantId: companyId, type: chatType },
    select: { chatId: true },
  });
  const candidateChatIds = [
    ...new Set(participants.map(({ chatId }) => chatId)),
  ];
  if (candidateChatIds.length === 0) return 0;

  const eventChats = (
    await mapInBatches(candidateChatIds, (batch) =>
      reportingPrisma.chat.findMany({
        where: { id: { in: batch }, eventId },
        select: { id: true },
      })
    )
  ).flat();
  const eventChatIds = eventChats.map(({ id }) => id);
  if (eventChatIds.length === 0) return 0;

  const counts = await mapInBatches(eventChatIds, (batch) =>
    reportingPrisma.message.count({
      where: withCreatedAt(
        {
          chatId: { in: batch },
          senderType: ChatParticipantType.USER,
        },
        range
      ),
    })
  );
  return counts.reduce((total, count) => total + count, 0);
}

export async function queryCompanyPortalNetworkingStats(
  eventId: string,
  companyId: string,
  companyType: "exhibitor" | "sponsor",
  filters: EventTierAnalyticsDbFilters
): Promise<EventTierCompanyPortalStats> {
  const range = analyticsCreatedAt(filters);
  if (range.mode === "match-none") {
    return { meetingsBooked: 0, messagesReceived: 0 };
  }

  const chatType = companyChatType(companyType);
  const meetingType = companyMeetingType(companyType);

  const [meetingsBooked, messagesReceived] = await Promise.all([
    countCompanyMeetings(eventId, companyId, meetingType, range),
    countCompanyMessagesReceived(eventId, companyId, chatType, range),
  ]);

  return { meetingsBooked, messagesReceived };
}
